/**
 * Markdown Renderer Utility
 * Safely renders markdown to HTML with sanitization
 *
 * Uses:
 * - marked.js for markdown parsing
 * - DOMPurify for XSS protection
 */

// Import marked and DOMPurify from node_modules (bundled by Vite)
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import page from '../vendor/page.js';

// Configure marked for safe therapeutic content rendering
marked.setOptions({
  breaks: true,        // Convert \n to <br> (GFM line breaks)
  gfm: true,          // GitHub Flavored Markdown
  headerIds: false,   // Disable auto-generated header IDs (security)
  mangle: false,      // Don't mangle email addresses
  pedantic: false,    // More forgiving parsing
  smartypants: false  // Don't convert quotes/dashes to typographic entities
});

/**
 * Render markdown text to safe HTML
 * @param {string} text - Markdown text to render
 * @returns {string} Sanitized HTML string
 */
export function renderMarkdown(text) {
  if (!text) return '';

  try {
    // Parse markdown to HTML
    const rawHtml = marked.parse(text);

    // Sanitize HTML to prevent XSS attacks
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
      // Allow only safe tags
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
        'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'a', 'span', 'hr'
      ],
      // Allow only safe attributes
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
      // Link target/rel policy is applied by the sanitizer hook below.
      ALLOW_DATA_ATTR: false,
      ADD_ATTR: ['target', 'rel']
    });

    return cleanHtml;
  } catch (error) {
    console.error('Markdown rendering error:', error);
    // Fallback to plain text if rendering fails
    return text;
  }
}

/**
 * Classify a markdown link using the same origin check as the SPA router.
 * Empty, hash-only, malformed, and non-HTTP(S) links are conservative externals.
 *
 * @param {string|null|undefined} href - Link destination
 * @returns {boolean} Whether page.js will treat the destination as same-origin
 */
export function isInternalAppLink(href) {
  if (typeof href !== 'string' || !href.trim() || href.trim().startsWith('#')) {
    return false;
  }

  try {
    return page.sameOrigin(href);
  } catch {
    return false;
  }
}

/**
 * Apply the markdown-link navigation policy to a sanitized anchor node.
 * Same-origin links are left targetless for page.js interception; every other
 * destination opens in a protected new tab.
 *
 * @param {Element} node - Sanitized DOM node
 * @returns {void}
 */
export function sanitizeLinkAttributes(node) {
  if (!node || node.nodeName !== 'A') return;

  const href = node.getAttribute('href');
  if (isInternalAppLink(href)) {
    node.removeAttribute('target');
    node.removeAttribute('rel');
  } else {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * Configure link behavior in rendered markdown.
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  sanitizeLinkAttributes(node);
});
